import { Howl } from 'howler';
import { captureMediaLoadError } from 'common/modules/media/captureMediaLoadError';
import { getHowlerContextState } from './howlerContext';
import { IAudioPlayer, IAudioPlayerOptions, tHowlAudioPlayerStatus } from './types';

/** Howler's private `Sound` shape; `_node` is a media element in html5 mode only. */
interface IHowlSoundInternals {
  _id?: unknown;
  _node?: unknown;
}

/**
 * Duck-typed rather than `instanceof HTMLMediaElement` so it holds across realms
 * and for test doubles. A `GainNode` has no `paused`, so this doubles as the
 * engine-mode check — the load-error fallback flips `this.html5` underneath us.
 */
function isPausableNode(node: unknown): node is { paused: boolean } {
  return (
    typeof node === 'object' &&
    node !== null &&
    typeof (node as { paused?: unknown }).paused === 'boolean'
  );
}

/**
 * Extensions Howler cannot map to a codec, keyed to the codec they hold. Howler
 * picks the codec from the URL extension alone, and the backend stores some MPEG
 * audio as `.mpga` — without this it rejects the source ("No codec support for
 * selected audio sources") before requesting it.
 */
const CODEC_BY_EXTENSION: Record<string, string> = {
  mpga: 'mp3'
};

function formatFor(src: string): string[] | undefined {
  const extension = /\.([^./]+)$/.exec(src.split(/[?#]/, 1)[0])?.[1].toLowerCase();
  const codec = extension && CODEC_BY_EXTENSION[extension];

  return codec ? [codec] : undefined;
}

export default class HowlAudioPlayer implements IAudioPlayer {
  private engine: Howl;

  private readonly options: IAudioPlayerOptions;

  private readonly progressInterval: number;

  private progressReq: number | null;

  private currentTrackId: number | undefined;

  private onProgress: IAudioPlayerOptions['onProgress'];

  private cachedSpeed: number;

  /** Engine mode; flips to false when we fall back to WebAudio on a load failure. */
  private html5: boolean;

  private didHtml5Fallback: boolean;

  private wantsPlay: boolean;

  private statusBeforePlay: tHowlAudioPlayerStatus;

  public status: tHowlAudioPlayerStatus;

  constructor(options: IAudioPlayerOptions) {
    const {
      src,
      html5 = true,
      speed = 1,
      progressInterval = 1000,
      autoplay = false
    } = options;

    if (!src.length) {
      throw new Error('Howl is not initialized');
    }

    this.options = options;
    this.progressInterval = progressInterval;
    this.onProgress = options.onProgress;
    this.progressReq = null;
    this.currentTrackId = undefined;
    this.cachedSpeed = speed ?? 1;
    this.html5 = html5;
    this.didHtml5Fallback = false;
    this.wantsPlay = autoplay;
    this.status = 'added';
    this.statusBeforePlay = 'added';

    this.engine = this.buildEngine(html5, autoplay);
  }

  private buildEngine(html5: boolean, autoplay: boolean): Howl {
    const {
      src,
      preload = 'metadata',
      loop = false,
      volume = 1,
      onStop,
      onPause,
      onLoad,
      onMute,
      onVolume,
      onRate,
      onSeek,
      onFade,
      onUnlock,
      onEnd,
      onPlay,
      onError
    } = this.options;

    const format = formatFor(src);

    return new Howl({
      src,
      ...(format && { format }),
      preload,
      autoplay,
      loop,
      volume,
      // Use the cached speed, not the original option, so a rebuilt fallback
      // engine keeps a playback rate the user changed at runtime via setSpeed().
      rate: this.cachedSpeed,
      html5,
      onstop: (soundId: number) => {
        this.status = 'stopped';

        this.stopAnimateProgress();

        onStop &&
          onStop({
            player: this,
            soundId
          });
      },
      onpause: (soundId: number) => {
        this.stopAnimateProgress();

        onPause &&
          onPause({
            player: this,
            soundId
          });
      },
      onload: (soundId: number) => {
        onLoad &&
          onLoad({
            player: this,
            soundId
          });
      },
      onmute: (soundId: number) => {
        onMute &&
          onMute({
            player: this,
            soundId
          });
      },
      onvolume: (soundId: number) => {
        onVolume &&
          onVolume({
            player: this,
            soundId
          });
      },
      onrate: (soundId: number) => {
        const engineRate = this.engine.rate();
        if (typeof engineRate === 'number') {
          this.cachedSpeed = engineRate;
        }

        onRate &&
          onRate({
            player: this,
            soundId
          });
      },
      onseek: (soundId: number) => {
        this.stopAnimateProgress();

        onSeek &&
          onSeek({
            player: this,
            soundId
          });

        this.startAnimateProgress();
      },
      onfade: (soundId: number) => {
        onFade &&
          onFade({
            player: this,
            soundId
          });
      },
      onunlock: (soundId: number) => {
        onUnlock &&
          onUnlock({
            player: this,
            soundId
          });
      },
      onend: (soundId: number) => {
        this.status = 'ended';

        this.onProgress && this.onProgress(100);

        this.stopAnimateProgress();

        onEnd &&
          onEnd({
            player: this,
            soundId
          });
      },
      onplay: (soundId: number) => {
        this.status = 'played';

        this.startAnimateProgress();

        onPlay &&
          onPlay({
            player: this,
            soundId
          });
      },
      onloaderror: (soundId, error) => {
        this.handleLoadError(error);
      },
      onplayerror: (soundId, error) => {
        // `play()` claimed 'played' synchronously but nothing started; without
        // restoring it the player stays invisible to `visibilityPlay()`/`stop()`.
        this.status = this.statusBeforePlay;

        captureMediaLoadError(error, {
          kind: 'audio',
          engine: 'howler',
          html5: this.html5,
          src: this.options.src,
          fallback: this.didHtml5Fallback
        });
        onError && onError(error);
      }
    });
  }

  /**
   * HTML5 mode relies on HTTP Range requests, which some CDN edges / proxies /
   * antivirus mangle into ERR_CONTENT_DECODING_FAILED and silent "no sound", so on
   * the first failure rebuild once in WebAudio mode (plain GET). Cf. 768976e8.
   */
  private handleLoadError(error: unknown): void {
    captureMediaLoadError(error, {
      kind: 'audio',
      engine: 'howler',
      html5: this.html5,
      src: this.options.src,
      fallback: this.didHtml5Fallback
    });

    if (this.html5 && !this.didHtml5Fallback) {
      this.didHtml5Fallback = true;
      this.html5 = false;

      const shouldResumePlay = this.wantsPlay;

      // Cancel the progress RAF bound to the old engine before discarding it.
      this.stopAnimateProgress();
      this.engine.unload();
      this.engine = this.buildEngine(false, false);

      // The rebuilt engine has no known sound ids yet, so resume the whole
      // sound rather than a stale id from the unloaded engine.
      if (shouldResumePlay) {
        this.currentTrackId = undefined;
        this.engine.play();
      }

      return;
    }

    this.options.onError && this.options.onError(error);
  }

  /**
   * Claims 'played' synchronously: Howler only reports playback via its async
   * `onplay`, so a second caller in the same dispatch (`visibilityPlay()` per
   * listener, a double tap, a re-entrant effect) saw the stale status, called
   * `play()` again, and got a fresh `Sound` — the track layered over itself.
   * `onplayerror` restores `statusBeforePlay` when nothing actually starts.
   */
  play(id?: number): void {
    this.currentTrackId = id;
    this.wantsPlay = true;

    this.statusBeforePlay = this.status;
    this.status = 'played';

    this.engine.play(id);
  }

  stop(id?: number): void {
    this.currentTrackId = id;
    this.wantsPlay = false;

    this.engine.stop(id);
  }

  pause(id?: number): void {
    this.currentTrackId = id;
    this.wantsPlay = false;

    this.status = 'paused';

    this.engine.pause(id);
  }

  visibilityPause(id?: number): void {
    this.pause(id);

    this.status = 'visible-paused';
  }

  togglePlay(id?: number): void {
    this.playing(id) ? this.pause(id) : this.play(id);
  }

  /**
   * Whether a sound is *really* playing — not whether Howler believes it is.
   * `Howl.playing()` is `!sound._paused`, and `_paused` is only ever written by
   * Howler's own API calls — it subscribes to no `pause` event and to no
   * audio-session notification — so an OS-initiated pause (call, Siri, a
   * recording taking the session) leaves it claiming playback forever, and
   * `togglePlay` spends the next tap on a `stop()` that stops nothing audible.
   *
   * Only ever *downgrades* a `true`: Howler legitimately knows about sounds we
   * cannot inspect (a play queued behind a pending load has no node yet). And
   * `status` stays untouched — it keeps the value `visibilityPlay()`/`stop()` use.
   */
  playing(id?: number): boolean {
    this.currentTrackId = id;

    const engineSaysPlaying = this.engine.playing(id);

    if (!engineSaysPlaying) return false;

    // `undefined` = undeterminable; keep the previous behaviour over a guess.
    return this.isAudible(id) ?? engineSaysPlaying;
  }

  /** Returns undefined rather than throwing on an unexpected internal shape. */
  private isAudible(id?: number): boolean | undefined {
    const nodes = this.getMediaNodes(id);

    if (!nodes) return undefined;

    // html5 mode: the media element does report an external pause.
    if (nodes.length) return nodes.some((node) => !node.paused);

    // WebAudio mode: no per-sound flag, but a `BufferSource` feeding a suspended
    // (iOS: `interrupted`) context is silent, and Howler's `_autoSuspend` bails on
    // any unpaused sound — so a suspended context means the OS took it.
    const contextState = getHowlerContextState();

    return contextState === undefined ? undefined : contextState === 'running';
  }

  /** html5-mode nodes for `id` (or all sounds); `[]` in WebAudio mode. */
  private getMediaNodes(id?: number): Array<{ paused: boolean }> | undefined {
    try {
      const sounds = (this.engine as unknown as { _sounds?: unknown })._sounds;

      if (!Array.isArray(sounds)) return undefined;

      return (sounds as IHowlSoundInternals[])
        .filter((sound) => typeof id !== 'number' || sound?._id === id)
        .map((sound) => sound?._node)
        .filter(isPausableNode);
    } catch (error) {
      return undefined;
    }
  }

  mute(mute: boolean, id?: number): void {
    this.engine.mute(mute, id);
  }

  getSpeed(): number {
    return this.cachedSpeed;
  }

  setSpeed(speed: number, id?: number): void {
    this.cachedSpeed = speed;
    this.currentTrackId = id;

    if (this.engine.state() === 'loaded') {
      this.engine.rate(speed);
    }
  }

  volume(value?: number): Howl | number | boolean {
    if (value !== undefined) {
      return this.engine.volume(value);
    } else {
      return this.engine.volume();
    }
  }

  unsubscribe(): void {
    this.onProgress = undefined;
  }

  /**
   * Unload and destroy a Howl object.
   * This will immediately stop all sounds attached to this sound and remove it from the cache.
   */
  unload(): void {
    this.beforeUnload();

    this.engine.unload();
  }

  beforeUnload(): void {
    this.stopAnimateProgress();

    this.unsubscribe();
  }

  private startAnimateProgress() {
    if (this.onProgress) {
      let start = Date.now();

      const animate = () => {
        if (start + this.progressInterval < Date.now()) {
          start = Date.now();
          const seek = this.engine.seek();

          if (typeof seek === 'number') {
            const progress = (seek / this.engine.duration(this.currentTrackId)) * 100;

            this.onProgress && this.onProgress(progress);
          }
        }

        this.progressReq = window.requestAnimationFrame(animate);
      };

      animate();
    }
  }

  private stopAnimateProgress() {
    if (this.progressReq) {
      cancelAnimationFrame(this.progressReq);
    }
  }

  seek(seek: number, id?: number): void {
    this.currentTrackId = id;

    this.engine.seek(seek);
  }
}
